import React, { useState, useEffect, useRef } from 'react';
import styles from './index.module.scss';
import { setPostImage } from '@images/imageUrls';
import Toast from '@components/Toast';
import Switch from '@components/switch';

interface ContentSettingsProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (settings: { aiGenerated: boolean; hasCopyright: boolean; scheduledPublish: boolean; publishTime?: string }) => void;
    initialSettings?: { aiGenerated: boolean; hasCopyright: boolean; scheduledPublish: boolean; publishTime?: string };
    canModifySchedule?: boolean; // 是否可以修改定时发布设置
}

const ContentSettingsH5: React.FC<ContentSettingsProps> = ({
    visible,
    onClose,
    onConfirm,
    initialSettings = { aiGenerated: false, hasCopyright: false, scheduledPublish: false, publishTime: '' },
    canModifySchedule = true, // 默认可以修改
}) => {
    const [aiGenerated, setAiGenerated] = useState(initialSettings.aiGenerated);
    const [hasCopyright, setHasCopyright] = useState(initialSettings.hasCopyright);
    const [scheduledPublish, setScheduledPublish] = useState(initialSettings.scheduledPublish);
    const [publishTime, setPublishTime] = useState(initialSettings.publishTime || '');
    const pickerRef = useRef<HTMLDivElement>(null);
    const maskRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!visible) return;

        const handleClickOutside = (e: TouchEvent | MouseEvent) => {
            if (maskRef.current === e.target) {
                onClose();
            }
        };

        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [visible, onClose]);

    // 重置初始值
    useEffect(() => {
        if (visible && initialSettings) {
            setAiGenerated(initialSettings.aiGenerated);
            setHasCopyright(initialSettings.hasCopyright);
            setScheduledPublish(initialSettings.scheduledPublish);
            setPublishTime(initialSettings.publishTime || '');
        }
    }, [visible, initialSettings]);

    if (!visible) return null;

    const handleConfirm = () => {
        if(scheduledPublish&&!publishTime){
            Toast.info('请选择定时发布时间')
            return
        }
        onConfirm({ aiGenerated, hasCopyright, scheduledPublish, publishTime: scheduledPublish ? publishTime : undefined });
        onClose();
    };

    // 格式化时间选择器的值
    const formatDateTimeLocal = (dateStr: string) => {
        if (!dateStr) return '';
        // 将 "2026-01-26 22:00" 转为 "2026-01-26T22:00"
        return dateStr.replace(' ', 'T');
    };

    // 处理时间变化
    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // 将 "2026-01-26T22:00" 转为 "2026-01-26 22:00"
        setPublishTime(value.replace('T', ' '));
    };

    // 获取最小可选时间（当前时间）
    const getMinDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return (
        <>
            <div className={styles.mask} ref={maskRef} />
            <div className={styles.settingsWrapper} ref={pickerRef}>
                <div className={styles.settingsHeader}>
                    <h2 className={styles.settingsTitle}>内容设置</h2>
                </div>

                <div className={styles.settingsContent}>
                    <div className={styles.settingItem}>
                        <div className={styles.settingLeft}>
                            <img src={setPostImage.settingDownload} alt="" />
                            <span className={styles.settingLabel}>版权保护</span>
                            <span className={styles.settingTip}>不允许他人下载图片</span>
                        </div>
                        <Switch checked={hasCopyright} onChange={setHasCopyright} />
                    </div>
                    <div className={styles.settingItem}>
                        <div className={styles.settingLeft}>
                           <img src={setPostImage.aiIcon} alt="" />
                            <span className={styles.settingLabel}>内容含AI生成元素</span>
                        </div>
                        <Switch checked={aiGenerated} onChange={setAiGenerated} />
                    </div>
                    <div className={styles.settingItem}>
                        <div className={styles.settingLeft}>
                            <img src={setPostImage.timeIcon} alt="" />
                            <span className={styles.settingLabel}>定时发布</span>
                        </div>
                        <Switch
                            checked={scheduledPublish}
                            disabled={!canModifySchedule}
                            onChange={(checked) => {
                                setScheduledPublish(checked);
                                if (checked && !publishTime) {
                                    const now = new Date();
                                    const y = now.getFullYear();
                                    const mo = String(now.getMonth() + 1).padStart(2, '0');
                                    const d = String(now.getDate()).padStart(2, '0');
                                    const h = String(now.getHours()).padStart(2, '0');
                                    const mi = String(now.getMinutes()).padStart(2, '0');
                                    setPublishTime(`${y}-${mo}-${d} ${h}:${mi}`);
                                }
                            }}
                        />
                    </div>

                    {scheduledPublish && (
                        <div className={styles.timeSelectItem}>
                            <div className={styles.timeSelectLeft}>
                                <img src={setPostImage.timeIconTime} alt="" className={styles.timeSelectIcon} />
                                <span className={styles.timeSelectLabel}>发布时间</span>
                            </div>
                            <input
                                type="datetime-local"
                                className={styles.timeInput}
                                value={formatDateTimeLocal(publishTime)}
                                onChange={handleTimeChange}
                                min={getMinDateTime()}
                                disabled={!canModifySchedule}
                                style={!canModifySchedule ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            />
                            <div className={styles.timeSelectRight}>
                                <span className={styles.timeSelectPlaceholder}>
                                    {publishTime ? publishTime : '选择发布时间'}
                                </span>
                                <span className={styles.arrow}>›</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.settingsFooter}>
                    <button className={styles.cancelBtn} onClick={onClose}>
                        取消
                    </button>
                    <button className={styles.confirmBtn} onClick={handleConfirm}>
                        确定
                    </button>
                </div>
            </div>
        </>
    );
};

export default ContentSettingsH5;
